/* eslint-disable @typescript-eslint/no-explicit-any */
import { Location } from '@angular/common';
import { DOCUMENT, Injectable, inject } from '@angular/core';
import { Router, RouterEvent } from '@angular/router';

import { AppConfig, BetslipBarConfig, EpcotConfig, EventDetailsConfig } from '@frontend/sports/common/client-config-data-access';
import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { NonRootToken } from '@frontend/sports/host-app/sports-product/feature/non-root-token';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import {
    DialogAnimation,
    ModalDialogOptions,
    ModalDialogService,
    ModalDismissReasons,
    ModalGestureAnimationService,
    ModalRef,
    OpenedModalsStorageService,
    PopupAction,
    PopupClosingResult,
} from '@frontend/sports/modal/feature';
import { NavigationHelper } from '@frontend/sports/navigation/core/feature/helpers';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { ModelPopupTypes, UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { UserService } from '@frontend/sports/user/feature';
import {
    HtmlNode,
    LocationChangeEvent,
    MediaQueryService,
    MenuAction,
    MenuActionsService,
    NavigationService,
    UrlService,
    WINDOW,
} from '@frontend/vanilla/core';
import { HeaderSearchService } from '@frontend/vanilla/shared/header';
import { Observable, Subject, distinctUntilKeyChanged, filter, map } from 'rxjs';

import { DISPATCHER_EVENT_ON_POPUP_OPENED, PopupTypes } from './models';
import { OVERLAY_INTEGRATION } from './overlay-integration';
import { SensitivePageTrackerService } from './sensitive-page-tracking.service';

interface PopupStack {
    name: PopupTypes;
    handle: ModalRef;
}

const popupsWithSkipLocationChange = new Set(['sports', 'bet-builder-overlay', 'event-switcher']);
const popupsAllowedOnLargeScreens = new Set([
    'betfinder',
    'sports',
    'grouped-sports',
    'archived-bets',
    'bet-builder-overlay',
    'hr-help-overlay',
    'gr-help-overlay',
    'ngrx-reports',
]);

@Injectable({ providedIn: 'root' })
export class PopupManager {
    private dialogService = inject(ModalDialogService);
    private sensitivePageTracker = inject(SensitivePageTrackerService);
    private htmlNode = inject(HtmlNode);
    private tracker = inject(TrackingService);
    private dispatcher = inject(DispatcherService);
    private navigationService = inject(NavigationService);
    private location = inject(Location);
    private router = inject(Router);
    private routerEventsService = inject(RouterEventsService);
    private urlService = inject(UrlService);
    private urlHelperService = inject(UrlHelperService);
    private mediaService = inject(MediaQueryService);
    private timerService = inject(TimerService);
    private headerSearchService = inject(HeaderSearchService);
    private epcotConfig = inject(EpcotConfig);
    private redirectHelper = inject(RedirectHelperService);
    private userService = inject(UserService);
    private menuActionsService = inject(MenuActionsService);
    private navigationHelper = inject(NavigationHelper);
    private appConfig = inject(AppConfig);
    private modalGestureAnimationService = inject(ModalGestureAnimationService);
    private openedModalsStorage = inject(OpenedModalsStorageService);
    private eventDetailsConfig = inject(EventDetailsConfig);

    get currentPopup(): PopupTypes | null {
        return this.popupStack.at(-1)?.name ?? null;
    }

    private popupStack: PopupStack[] = [];
    private largeScreen = false;
    private popups: { [key: string]: { component: any; options: ModalDialogOptions<any> } } = {};
    private _setLastActiveItem = new Subject<void>();
    private opening = false;

    private readonly _doc = inject(DOCUMENT);
    private readonly _window = inject(WINDOW);
    private betslipBarConfig = inject(BetslipBarConfig);
    private readonly overlayIntegration = inject(NonRootToken(OVERLAY_INTEGRATION));

    constructor() {
        const productUtils = inject(SportsProductUtils);

        this.navigationService.locationChange.pipe(productUtils.filterSports()).subscribe((e: LocationChangeEvent) => this.locationChangeHandler(e));

        // adding a reference to the layout module is creating a circular dependency
        this.mediaService
            .observe()
            .pipe(
                productUtils.filterSportsEmitLast(),
                map(() => ({ large: this.mediaService.isActive('gt-md') })),
                distinctUntilKeyChanged('large'),
            )
            .subscribe((screen) => this.screenSizeChanged(screen));

        if (this.epcotConfig.isEnabled) {
            this.headerSearchService.clickEvent.pipe(productUtils.filterSports()).subscribe((_) => {
                this.open('betfinder');
            });
        }

        this.router.events
            .pipe(
                productUtils.filterSports(),
                filter((event) => event instanceof RouterEvent),
            )
            .subscribe((event) => {
                if (!(event instanceof RouterEvent)) {
                    return;
                }
                const { search } = this.urlService.parse(event.url);
                if (search.has('popup')) {
                    const requestedPopup = search.get('popup') as PopupTypes;
                    for (let i = this.popupStack.length - 1; i >= 0; i--) {
                        if (this.popupStack[i].name === requestedPopup) {
                            break;
                        }
                        this.close(PopupAction.CloseByNavigation);
                    }

                    return;
                }

                if (this.isOpen()) {
                    this.closeAll();
                }
            });
    }

    get setLastActiveItem(): Observable<unknown> {
        return this._setLastActiveItem.asObservable();
    }

    configure(name: PopupTypes, component: any, options: ModalDialogOptions<any>): void {
        if (this.popups[name]) {
            throw new Error(`Popup '${name}' has already been configured`);
        }

        this.popups[name] = {
            component,
            options,
        };
    }

    updateDialogAnimation(name: PopupTypes, animationType?: DialogAnimation, showBackNav = false, closePreviousModalWindow = false): void {
        const popupConfig = this.popups[name];
        if (popupConfig?.options?.settings) {
            popupConfig.options.settings.animation = animationType;
            popupConfig.options.settings.showBackNav = showBackNav;
            popupConfig.options.settings.closePrevious = closePreviousModalWindow;
        }
    }

    updateDialogTitle(name: PopupTypes, title: string): void {
        this.popupStack.find((p) => p.name === name)?.handle.updateTitle(title);
    }

    open(name: PopupTypes, animationType?: DialogAnimation, options?: ModalDialogOptions<any>): void {
        if (this.opening) {
            return;
        }

        try {
            this.opening = true;
            if (!this.popups[name]) {
                throw new Error(`Popup '${name}' cannot be opened as it has not been configured`);
            }

            const popupAlreadyOpened = this.popupStack.some((ps) => ps.name === name);
            if (popupAlreadyOpened) {
                this.opening = false;

                return;
            }

            if (this.popups[name].options.settings?.needsAuthentication && !this.userService.isAuthenticated) {
                this.handlePopupUnauth(name);
                this.opening = false;

                return;
            }

            const parentPopupCloseDelay = 50;

            if (this.currentPopup) {
                const popupConfig = this.popups[this.currentPopup];
                const closePrevious = this.popups[name]?.options?.settings?.closePrevious;
                const preventClosePreviously = options?.settings?.closePrevious === false;
                if (!preventClosePreviously && (popupConfig.options.settings?.closeOnChildPopup || closePrevious)) {
                    this.close(PopupAction.CloseByNextPopup);
                }
                if (name === ModelPopupTypes.MyBets && animationType && popupConfig.options.settings) {
                    popupConfig.options.settings.animation = animationType;
                }
            }

            if (options) {
                this.popups[name].options = {
                    data: { ...this.popups[name].options.data, ...options.data },
                    settings: { ...this.popups[name].options.settings, ...options.settings },
                };
            }

            this.timerService.setTimeout(async () => {
                try {
                    await this.openHandler(name);
                } finally {
                    this.opening = false;
                }
            }, parentPopupCloseDelay);
        } catch (error) {
            this.opening = false;
            throw error;
        }
    }

    closeByName(action: PopupAction = PopupAction.ManualClose, popupName: PopupTypes): Promise<void> {
        const index = this.popupStack?.findIndex((p) => p.name === popupName);

        return this.close(action, index);
    }

    close(action: PopupAction = PopupAction.ManualClose, index = -1): Promise<void> {
        const popupHandle = index >= 0 ? this.popupStack[index] : this.popupStack.at(-1);

        if (!popupHandle) {
            return Promise.resolve();
        }

        const isBabPopup = popupHandle.name === 'bet-builder-overlay';

        if (this.appConfig.modalDialogGesturesEnabled && action !== PopupAction.SilentClose) {
            this.modalGestureAnimationService.slideOutToBottom({ modalWindow: popupHandle.handle.windowRef }).finally(() => {
                popupHandle.handle.close({ closeAction: action });
            });
        } else {
            popupHandle.handle.close({ closeAction: action });
        }

        this.openedModalsStorage.tryCloseSeeAll(isBabPopup);

        return popupHandle.handle.result;
    }

    dismissPreviousPopup(popupName: PopupTypes, action: PopupAction = PopupAction.ManualClose, windowClass = ''): Promise<void> {
        const popupHandle = this.popupStack?.find((p) => p.name === popupName);

        if (!popupHandle) {
            return Promise.resolve();
        }

        if (windowClass) {
            if (this.appConfig.modalDialogGesturesEnabled) {
                this.modalGestureAnimationService.slideOutToBottom({ modalWindow: popupHandle.handle.windowRef }).finally(() => {
                    popupHandle.handle.dismiss({ closeAction: action });
                });
            } else {
                popupHandle.handle.addWindowClass(windowClass);
                this.timerService.setTimeoutOutsideAngular(() => {
                    popupHandle.handle.dismiss({ closeAction: action });
                }, 500);
            }
        } else {
            popupHandle.handle.dismiss({ closeAction: action });
        }

        return popupHandle.handle.result;
    }

    closeAll(): void {
        for (let index = this.popupStack.length - 1; index >= 0; index--) {
            this.close(PopupAction.ManualClose, index);
        }

        this.popupStack = [];
    }

    toggle(name: PopupTypes): void {
        if (this.isOpen()) {
            this.close();
        } else {
            this.open(name);
        }
    }

    isOpen(): boolean {
        return this.popupStack.length > 0;
    }

    openBetslip(fromBottomNav?: boolean): void {
        if (fromBottomNav) {
            this.tracker.track(trackingConstants.EVENT_PAGE_VIEW, {
                [trackingConstants.PAGE_NAME]: 'M2_slip_open_bottomBubble',
                [trackingConstants.PAGE_URL]: 'in11288',
            });
        } else {
            this.tracker.track(trackingConstants.EVENT_PAGE_VIEW, {
                [trackingConstants.PAGE_NAME]: 'M2_slip_open_topRightButton',
                [trackingConstants.PAGE_URL]: 'in11246',
            });
        }

        this.open(ModelPopupTypes.Betslip);
    }

    isPopupOpen(popupName: PopupTypes): boolean {
        return popupName === this.currentPopup;
    }

    isPopupInStack(popupName: PopupTypes): boolean {
        return this.popupStack.some((popup) => popup.name === popupName);
    }

    private screenSizeChanged = (screen: { large: boolean }): void => {
        this.largeScreen = screen.large;
        if (screen.large && this.currentPopup === ModelPopupTypes.Betslip) {
            this.close(PopupAction.ManualClose);
        }
    };

    private async openInternal(name: PopupTypes | null): Promise<void> {
        if (!name) {
            throw new Error(`Trying to open popup with no name`);
        }

        if (!this.popups[name]) {
            throw new Error(`Popup '${name}' cannot be opened as it has not been configured`);
        }

        const popupConfig = this.popups[name];
        popupConfig.options.settings = popupConfig.options.settings ?? {};
        if (name === 'sports') {
            popupConfig.options.settings.container = this._doc.querySelector<HTMLElement>('.az-menu-container') || undefined;
        }
        const component = typeof popupConfig.component === 'function' ? await popupConfig.component() : popupConfig.component;
        const handle = this.dialogService.openPopup(component, popupConfig.options);
        let currentUrl = this.navigationService.location.path();
        if (currentUrl === this.urlHelperService.getAtoZUrl()) {
            currentUrl = this.urlHelperService.getHomeUrl();
        }
        const currentQueryParams = this.navigationService.location.absUrl().split('?')[1];
        const queryParams = `popup=${name}`;
        if (currentQueryParams !== queryParams && !this.overlayIntegration.isOverlayOpen()) {
            this.location.go(currentUrl, queryParams);
        }
        this.popupStack.push({ name, handle });
        this.show();

        handle.result.then((r) => this.onClosing(r, name)).catch((error) => this.onClosing(error, name));

        this.dispatcher.dispatch(DISPATCHER_EVENT_ON_POPUP_OPENED, name);
    }

    private locationChangeHandler(e: LocationChangeEvent): void {
        if (e.nextUrl) {
            const nextUrl = this.urlService.parse(e.nextUrl);
            if (nextUrl.search.has('popup')) {
                let requestedPopup = nextUrl.search.get('popup') as PopupTypes;
                if (this.shouldSkipLocationChange(requestedPopup)) {
                    return;
                }

                if (this.betslipBarConfig.enableIntegratedSheetView && ['my-bets', 'betslip'].includes(requestedPopup)) {
                    requestedPopup = ModelPopupTypes.BetModal;
                }

                const popupInStack = this.popupStack.some((ps) => ps.name === requestedPopup);

                if (!popupInStack && (!this.largeScreen || this.allowedInLargeScreen(requestedPopup))) {
                    this.open(requestedPopup);
                }

                return;
            }
        }
        this.closeAll();

        const previousUrl = this.urlService.parse(e.previousUrl);
        if (previousUrl.search.has('popup') && previousUrl.search.get('popup') === 'sports') {
            this._setLastActiveItem.next();
        }
    }

    private handlePopupUnauth(name: string): void {
        const returnUrl = `?popup=${name}`;
        /* 
            Switching to the home URL as the reference for popup navigation. 
            This is necessary because if the popup URL remains unchanged after login, the locationChangeHandler won't detect it to trigger popup opening. 
        */
        this.navigationService.goTo(this.urlHelperService.getHomeUrl(), { replace: true });

        // On successful login url locationchanger can handle the popup as the url changes
        this.menuActionsService.invoke(MenuAction.GOTO_LOGIN, '', [undefined, undefined, { returnUrl }]);
    }

    private shouldSkipLocationChange(popup: PopupTypes): boolean {
        return popupsWithSkipLocationChange.has(popup);
    }

    private allowedInLargeScreen(popup: PopupTypes): boolean {
        return popupsAllowedOnLargeScreens.has(popup);
    }

    private openHandler = async (name: PopupTypes | null): Promise<void> => {
        const oldPopup = this.popupStack.at(-1)?.name ?? '';
        await this.openInternal(name);
        this.sensitivePageTracker.dispatchSensitivePagesEvent(name, oldPopup);
    };

    private onClosing = (result: PopupClosingResult | ModalDismissReasons, name: PopupTypes) => {
        const previousPopUp = this.popupStack[0];
        this.popupStack = this.popupStack.filter((p) => p.name !== name);

        this.dispatcher.dispatch('ON_POPUP_CLOSED', name);

        if ((result as PopupClosingResult).closeAction === PopupAction.CloseByPan) {
            this.navigateOnClose();
        }

        if (this.popupStack.length === 0) {
            // last popup was closed
            this.hide();
        }

        if (!this.isClosedManually(result)) {
            // navigation occurred so we do nothing
            return;
        }

        if (this.isFirstLoadAndClosingBetslipPopup(previousPopUp.name)) {
            const oldPopup = this.popupStack.at(-1)?.name ?? null;

            this.router.navigate([this.navigationService.location.path()], {
                queryParams: { popup: oldPopup },
                queryParamsHandling: 'merge',
                replaceUrl: true,
            });

            return;
        }

        if (!(previousPopUp.name === 'betfinder' && this.currentPopup === 'betslip')) {
            if (previousPopUp.name === 'my-bets' && this.epcotConfig.isEnabled && this._window.history.length <= 2) {
                this.redirectHelper.goHome(undefined, true);
            } else {
                this.navigateOnClose();
            }
        }
    };

    private navigateOnClose(): void {
        if (this.eventDetailsConfig.showInOverlay && this.currentPopup === 'event-switcher') {
            this.timerService.setTimeoutOutsideAngular(() => {
                const url = this.routerEventsService.urlHistory?.[this.routerEventsService.urlHistory.length - 1];
                this.location.replaceState(url);
            });
        } else {
            if (this.popupStack.length > 0) {
                this.location.back();
            } else {
                this.removePopupSearchParam();
            }
        }
    }

    private removePopupSearchParam(): void {
        const params = new URLSearchParams(this._window.location.search);
        params.delete('popup');

        const url = new URL(this._window.location.href);
        url.search = params.toString();
        this._window.history.replaceState(null, '', url);
    }

    private isFirstLoadAndClosingBetslipPopup(closingPopup: string): boolean {
        return !this.navigationHelper.allowBack() && closingPopup === 'betslip';
    }

    private isClosedManually(result: PopupClosingResult | ModalDismissReasons): boolean {
        if (this.isPopupActionResult(result)) {
            if (result.closeAction === PopupAction.ManualClose) {
                return true;
            }
        } else {
            if (result === ModalDismissReasons.BackdropClick || result === ModalDismissReasons.Esc) {
                return true;
            }
        }

        return false;
    }

    private hide(): void {
        this.htmlNode.setCssClass('popup-on', false);
    }

    private show(): void {
        if (this.currentPopup === 'sports') {
            this.htmlNode.setCssClass('popup-on az-menu-desktop', true);

            return;
        }
        this.htmlNode.setCssClass('popup-on', true);
    }

    private isPopupActionResult(result: PopupClosingResult | ModalDismissReasons): result is PopupClosingResult {
        return !!(result as PopupClosingResult).closeAction;
    }
}
